import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { trpc } from "@/lib/trpc";
import { useEffect, useState, useMemo } from "react";
import { useLocation } from "wouter";
import { 
  AlertTriangle, 
  BarChart3, 
  Clock, 
  TrendingUp,
  ArrowLeft,
  UserCog,
  Package,
  MapPin
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";

export default function Dashboard() {
  const { user, loading: authLoading } = useGoogleAuth();
  const [, setLocation] = useLocation();
  const [topLimit, setTopLimit] = useState(10);
  const [periodFilter, setPeriodFilter] = useState<'today' | 'week' | 'month' | 'custom'>('month');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  // Calcular datas baseado no filtro
  const getDateRange = () => {
    const now = new Date();
    let start: Date | undefined;
    let end: Date | undefined = now;

    switch (periodFilter) {
      case 'today':
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        break;
      case 'week':
        start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case 'month':
        start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case 'custom':
        if (startDate) start = new Date(startDate);
        if (endDate) end = new Date(endDate);
        break;
    }

    return { startDate: start, endDate: end };
  };

  const dateRange = useMemo(() => getDateRange(), [periodFilter, startDate, endDate]);

  // Redirecionar para login se não autenticado
  useEffect(() => {
    if (!authLoading && !user) {
      window.location.href = "/login";
    }
  }, [user, authLoading]);

  // Queries do dashboard com auto-refresh a cada 30 segundos
  const { data: performance = [] } = trpc.dashboard.performanceByResponsavel.useQuery(
    { startDate: dateRange.startDate, endDate: dateRange.endDate },
    { 
      enabled: !!user && (user.role === "lider" || user.role === "admin"),
      refetchInterval: 30000, // 30 segundos
    }
  );

  const { data: movementsByTipo = [] } = trpc.dashboard.movementsByTipo.useQuery(
    { startDate: dateRange.startDate, endDate: dateRange.endDate },
    { 
      enabled: !!user && (user.role === "lider" || user.role === "admin"),
      refetchInterval: 30000,
    }
  );

  const { data: criticalItems = [] } = trpc.dashboard.criticalItems.useQuery(
    undefined,
    { 
      enabled: !!user && (user.role === "lider" || user.role === "admin"),
      refetchInterval: 30000,
    }
  );

  const { data: averageTime = [] } = trpc.dashboard.averageTimeByTipo.useQuery(
    { startDate: dateRange.startDate, endDate: dateRange.endDate },
    { 
      enabled: !!user && (user.role === "lider" || user.role === "admin"),
      refetchInterval: 30000,
    }
  );

  const { data: averageTimeByOperator = [] } = trpc.dashboard.averageTimeByOperator.useQuery(
    { startDate: dateRange.startDate, endDate: dateRange.endDate },
    { 
      enabled: !!user && (user.role === "lider" || user.role === "admin"),
      refetchInterval: 30000,
    }
  );

  const { data: topProducts = [] } = trpc.dashboard.topProducts.useQuery(
    { limit: topLimit, startDate: dateRange.startDate, endDate: dateRange.endDate },
    { 
      enabled: !!user && (user.role === "lider" || user.role === "admin"),
      refetchInterval: 30000,
    }
  );

  // DEBUG
  console.log('[DASHBOARD] user:', user);
  console.log('[DASHBOARD] performance:', performance);
  console.log('[DASHBOARD] movementsByTipo:', movementsByTipo);

  // Função para exportar ranking de produtos para Excel
  const handleExportTopProducts = async () => {
    const XLSX = await import('xlsx');
    const worksheet = XLSX.utils.json_to_sheet(
      topProducts.map((item, index) => ({
        'Posição': index + 1,
        'Código': item.productCod,
        'Descrição': item.productDescription || '-',
        'Quantidade de Movimentações': item.count,
      }))
    );
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Produtos Mais Movimentados');
    XLSX.writeFile(workbook, `ranking-produtos-${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }


  const getTipoColor = (tipo: string) => {
    switch (tipo) {
      case "ENTRADA":
        return "bg-purple-500";
      case "INTERNA":
        return "bg-blue-500";
      case "SAIDA":
        return "bg-green-500";
      case "REPOSICAO":
        return "bg-amber-500";
      default:
        return "bg-gray-500";
    }
  };

  const getTipoLabel = (tipo: string) => {
    switch (tipo) {
      case "ENTRADA":
        return "Entrada";
      case "INTERNA":
        return "Interna";
      case "SAIDA":
        return "Saída";
      case "REPOSICAO":
        return "Reposição";
      default:
        return tipo;
    }
  };

  // Calcular total para percentuais
  const totalMovements = movementsByTipo.reduce((acc, item) => acc + Number(item.count), 0);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-card border-b shadow-sm">
        <div className="container py-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <h1 className="text-2xl font-bold text-foreground">
              Dashboard do Líder
            </h1>
            
            {/* Filtro de Período */}
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-muted-foreground">Período:</label>
              <select
                value={periodFilter}
                onChange={(e) => setPeriodFilter(e.target.value as any)}
                className="px-3 py-1.5 text-sm border rounded-md bg-background"
              >
                <option value="today">Hoje</option>
                <option value="week">Última Semana</option>
                <option value="month">Último Mês</option>
                <option value="custom">Customizado</option>
              </select>
              
              {periodFilter === 'custom' && (
                <>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="px-2 py-1 text-sm border rounded-md bg-background"
                    placeholder="Início"
                  />
                  <span className="text-muted-foreground">até</span>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="px-2 py-1 text-sm border rounded-md bg-background"
                    placeholder="Fim"
                  />
                </>
              )}
              
              <div className="text-sm text-muted-foreground">
                Atualização automática a cada 30s
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container py-6 space-y-6">
        {/* Grid de Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Card: Desempenho por Responsável */}
          <div className="bg-card rounded-lg border shadow-sm p-6">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="h-5 w-5 text-primary" />
              <h2 className="text-lg font-semibold">Desempenho por Responsável</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground">
                      Responsável
                    </th>
                    <th className="text-center py-2 px-3 text-xs font-semibold text-muted-foreground">
                      Total
                    </th>
                    <th className="text-center py-2 px-3 text-xs font-semibold text-muted-foreground">
                      Concluídos
                    </th>
                    <th className="text-center py-2 px-3 text-xs font-semibold text-muted-foreground">
                      Em Andamento
                    </th>
                    <th className="text-center py-2 px-3 text-xs font-semibold text-muted-foreground">
                      Abertos
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {performance.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center py-4 text-muted-foreground text-sm">
                        Nenhum dado disponível
                      </td>
                    </tr>
                  ) : (
                    performance.map((item, index) => (
                      <tr key={index} className="hover:bg-muted/30">
                        <td className="py-2 px-3 text-sm font-medium">
                          {item.responsavel || "Não atribuído"}
                        </td>
                        <td className="py-2 px-3 text-sm text-center">
                          {item.total}
                        </td>
                        <td className="py-2 px-3 text-sm text-center text-green-600">
                          {item.concluidos}
                        </td>
                        <td className="py-2 px-3 text-sm text-center text-blue-600">
                          {item.emAndamento}
                        </td>
                        <td className="py-2 px-3 text-sm text-center text-orange-600">
                          {item.abertos}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Card: Movimentações por Tipo */}
          <div className="bg-card rounded-lg border shadow-sm p-6">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className="h-5 w-5 text-primary" />
              <h2 className="text-lg font-semibold">Movimentações por Tipo</h2>
            </div>
            <div className="space-y-3">
              {movementsByTipo.length === 0 ? (
                <p className="text-center py-4 text-muted-foreground text-sm">
                  Nenhum dado disponível
                </p>
              ) : (
                movementsByTipo.map((item, index) => {
                  const percentage = totalMovements > 0 
                    ? ((Number(item.count) / totalMovements) * 100).toFixed(1) 
                    : 0;
                  
                  return (
                    <div key={index} className="space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium">{getTipoLabel(item.tipo)}</span>
                        <span className="text-muted-foreground">
                          {item.count} ({percentage}%)
                        </span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${getTipoColor(item.tipo)}`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Card: Itens Críticos (> 4h) */}
          <div className="bg-card rounded-lg border shadow-sm p-6">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              <h2 className="text-lg font-semibold">Itens Críticos (&gt; 4h Abertos)</h2>
            </div>
            <div className="space-y-2">
              {criticalItems.length === 0 ? (
                <p className="text-center py-4 text-muted-foreground text-sm">
                  Nenhum item crítico no momento
                </p>
              ) : (
                criticalItems.map((item) => (
                  <div
                    key={item.id}
                    className="p-3 bg-red-50 border border-red-200 rounded-lg"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-red-900">
                          #{item.id} - {item.produto}
                        </p>
                        <p className="text-xs text-red-700 mt-1">
                          {item.origem} → {item.destino}
                        </p>
                        <p className="text-xs text-red-600 mt-1">
                          Atribuído para: {item.responsavel || "Não atribuído"}
                        </p>
                      </div>
                      <div className="text-xs text-red-600 font-medium">
                        {formatDistanceToNow(new Date(item.criadoEm), {
                          addSuffix: true,
                          locale: ptBR,
                        })}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Card: Tempo Médio por Tipo */}
          <div className="bg-card rounded-lg border shadow-sm p-6">
            <div className="flex items-center gap-2 mb-4">
              <Clock className="h-5 w-5 text-primary" />
              <h2 className="text-lg font-semibold">Tempo Médio de Conclusão</h2>
            </div>
            <div className="space-y-3">
              {averageTime.length === 0 ? (
                <p className="text-center py-4 text-muted-foreground text-sm">
                  Nenhum dado disponível
                </p>
              ) : (
                averageTime.map((item, index) => {
                  const avgMinutes = Number(item.avgMinutes) || 0;
                  const hours = Math.floor(avgMinutes / 60);
                  const minutes = Math.round(avgMinutes % 60);
                  
                  return (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${getTipoColor(item.tipo)}`} />
                        <span className="text-sm font-medium">
                          {getTipoLabel(item.tipo)}
                        </span>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold">
                          {hours > 0 ? `${hours}h ${minutes}min` : `${minutes}min`}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {item.count} concluídos
                        </p>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Card: Performance por Operador */}
          <div className="bg-card rounded-lg border shadow-sm p-6">
            <div className="flex items-center gap-2 mb-4">
              <UserCog className="h-5 w-5 text-primary" />
              <h2 className="text-lg font-semibold">Performance por Operador</h2>
            </div>
            <div className="space-y-3">
              {averageTimeByOperator.length === 0 ? (
                <p className="text-center py-4 text-muted-foreground text-sm">
                  Nenhum dado disponível
                </p>
              ) : (
                (() => {
                  // Calcular média geral
                  const totalAvgHours = averageTimeByOperator.reduce((sum, item) => sum + (Number(item.avgHours) || 0), 0);
                  const overallAvg = totalAvgHours / averageTimeByOperator.length;
                  
                  return averageTimeByOperator.map((item, index) => {
                    const avgHours = Number(item.avgHours) || 0;
                    const hours = Math.floor(avgHours);
                    const minutes = Math.round((avgHours - hours) * 60);
                    const isAboveAverage = avgHours > overallAvg;
                    
                    return (
                      <div
                        key={index}
                        className={`flex items-center justify-between p-3 rounded-lg ${
                          isAboveAverage ? 'bg-red-500/10 border border-red-500/20' : 'bg-muted/30'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          {isAboveAverage && (
                            <AlertTriangle className="h-4 w-4 text-red-500" />
                          )}
                          <span className="text-sm font-medium">
                            {item.operatorName || 'Sem nome'}
                          </span>
                        </div>
                        <div className="text-right">
                          <p className={`text-sm font-semibold ${
                            isAboveAverage ? 'text-red-600' : ''
                          }`}>
                            {hours > 0 ? `${hours}h ${minutes}min` : `${minutes}min`}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {item.count} concluídos
                          </p>
                        </div>
                      </div>
                    );
                  });
                })()
              )}
            </div>
            {averageTimeByOperator.length > 0 && (
              <div className="mt-4 pt-4 border-t">
                <p className="text-xs text-muted-foreground">
                  <AlertTriangle className="h-3 w-3 inline mr-1 text-red-500" />
                  Operadores acima da média são destacados em vermelho
                </p>
              </div>
            )}
          </div>

          {/* Card: Top Produtos Mais Movimentados */}
          <div className="bg-card rounded-lg border shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Package className="h-5 w-5 text-primary" />
                <h2 className="text-lg font-semibold">Produtos Mais Movimentados</h2>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm text-muted-foreground">Top:</label>
                <input
                  type="number"
                  min="1"
                  max="1000"
                  value={topLimit}
                  onChange={(e) => setTopLimit(Math.max(1, Math.min(1000, parseInt(e.target.value) || 10)))}
                  className="w-20 px-2 py-1 text-sm border rounded-md bg-background"
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleExportTopProducts}
                  disabled={topProducts.length === 0}
                >
                  Exportar Excel
                </Button>
              </div>
            </div>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {topProducts.length === 0 ? (
                <p className="text-center py-4 text-muted-foreground text-sm">
                  Nenhum dado disponível
                </p>
              ) : (
                topProducts.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary font-semibold text-sm">
                        {index + 1}
                      </div>
                      <div>
                        <p className="text-sm font-medium">
                          {item.productCod}
                        </p>
                        <p className="text-xs text-muted-foreground truncate max-w-xs">
                          {item.productDescription || 'Sem descrição'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-primary">
                        {item.count}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        movimentações
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
